import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';

import { AppButton } from '@/components/atoms';
import {
  floatingCtaSx,
  headerCtaSx,
  MenuIcon,
  primaryCtaPx,
  secondaryCtaPx,
  ThemeModeToggle,
} from '@/components/molecules';
import { consultationCta, quoteCta } from '@/config/navigation';
import { FIT, fitMark } from '@/hooks/useHeaderFit';

import { AccessoryOrderButton } from './AccessoryOrderButton';
import { CarOrderButton } from './CarOrderButton';

type Props = {
  /** Text colour for the bar's current state: ink when solid, white while floating. */
  ink: string;
  /** False only while the bar floats over the dark home hero. */
  solid: boolean;
  /**
   * Whether the row has room for the outlined secondary CTA, from
   * `useHeaderFit`. See the note on the pill below for why this is measured
   * and not a breakpoint.
   */
  showSecondaryCta: boolean;
  onOpenMenu: () => void;
};

/**
 * The right-hand end of the header: the car and accessory carts, the theme toggle, the
 * two calls to action and the menu button.
 *
 * Extracted from `SiteHeader` when the consultation pill joined it, and worth
 * extracting because this cluster is the part of the bar that keeps growing
 * while the rest of it — the ribbon, the utility strip, the nav — does not.
 * `SiteHeader` is now the arrangement of four rows and this file is the one
 * that knows what the controls are, which is the split that stops a second
 * CTA turning the header into a 250-line component.
 *
 * It holds no state. The drawer belongs to `SiteHeader`, because the drawer is
 * lazily fetched and the header is what decides when to pay for it, so the
 * menu button reports a press and nothing more.
 *
 * ## Which controls exist at which width
 *
 * Two filled pills side by side would be two primaries, and IA §5 allows one
 * per screen — so the consultation pill is outlined and the quotation one
 * keeps the fill. Rank, not equality: a visitor who knows the car they want
 * is worth more to Kabex than one who wants to talk, and the quotation page
 * is where the first one goes.
 *
 * The outline is the brand red (`primaryOutline`), not the ink, so the pill
 * is recognisably the same button here, in the drawer and in the phone action
 * bar. Only the home hero needs an exception: the red is under AA on
 * near-black, so while the bar floats the pill borrows white (`floatingCtaSx`).
 *
 * The outlined pill is `lg` and up only, which is the same breakpoint the
 * desktop nav appears at. Below it the bar holds the logo, the list badge,
 * one pill and the hamburger, and on a 360px phone that is already tight;
 * a second pill there would push the menu button off the row. So below `lg`
 * the consultation route is a full-width button at the top of the drawer
 * (`MobileNavDrawer`), where it has room to be the equal of the quotation one
 * rather than the half of it that got cut. It is also in the footer's Company
 * column, which renders on every page at every width.
 *
 * From `lg` up the pill is conditional on `showSecondaryCta` as well as the
 * breakpoint, and that is the fix for a real bug rather than a refinement.
 * `lg` is 1,200px, and the full row — logo, seven nav items, list badge,
 * toggle and both pills — measured 1,214px against 1,152px of usable toolbar:
 * "Get a Quote" hung 38px off the right edge of every 1,200px laptop, and
 * because the bar is `position: fixed` it did so without a scrollbar to show
 * for it. Trimming the type and padding bought most of that back (see
 * `navItemSx`), but not enough to also carry the quotation-list badge, which
 * arrives at 52px the moment a visitor saves a car.
 *
 * So this pill is the designated loser. It is the only control on the row with
 * somewhere else to be — the drawer and the footer both carry it on every page
 * at every width — which makes it the one thing that can be dropped at a tight
 * width without costing a visitor a route. `useHeaderFit` drops it before it
 * will fold a single nav item.
 */
export function HeaderActions({ ink, solid, showSecondaryCta, onOpenMenu }: Props) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={2}
      {...fitMark(FIT.actions)}
      sx={{ ml: { xs: 'auto', lg: 0 }, flexShrink: 0 }}
    >
      <CarOrderButton color={ink} />
      <AccessoryOrderButton color={ink} />

      {/* Below lg the toggle lives in the drawer. */}
      <Box sx={{ display: { xs: 'none', lg: 'block' }, color: ink }}>
        <ThemeModeToggle />
      </Box>

      {showSecondaryCta && (
        <AppButton
          to={consultationCta.to}
          appVariant="primaryOutline"
          {...fitMark(FIT.cta)}
          sx={[
            headerCtaSx,
            { display: { xs: 'none', lg: 'inline-flex' }, px: secondaryCtaPx },
            !solid && floatingCtaSx,
          ]}
        >
          {consultationCta.label}
        </AppButton>
      )}

      <AppButton to={quoteCta.to} sx={[headerCtaSx, { px: primaryCtaPx }]}>
        {quoteCta.label}
      </AppButton>

      <IconButton
        aria-label="Open menu"
        onClick={onOpenMenu}
        sx={{ display: { lg: 'none' }, color: ink }}
      >
        <MenuIcon />
      </IconButton>
    </Stack>
  );
}
